import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Clock, ArrowRight, CheckCircle, XCircle, Trophy, History, ChevronDown, ChevronUp, ArrowLeft, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function MockTestsPage() {
  const [tests, setTests] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [activeTest, setActiveTest] = useState(null);
  const [testState, setTestState] = useState("list"); // "list" | "taking" | "result"

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testsRes, historyRes] = await Promise.all([
          api.get("/mock-tests"),
          api.get("/mock-test-attempts/my-history").catch(() => ({ data: [] })),
        ]);
        setTests(testsRes.data || []);
        setHistory(historyRes.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const startTest = async (test) => {
    try {
      const { data } = await api.get(`/mock-tests/${test.id}`);
      setActiveTest(data);
      setTestState("taking");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to load test");
    }
  };

  const handleTestComplete = () => {
    setActiveTest(null);
    setTestState("list");
    // Refresh history
    api.get("/mock-test-attempts/my-history").then(({ data }) => setHistory(data || [])).catch(() => {});
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  if (testState === "taking" && activeTest) {
    return <MockTestPlayer test={activeTest} onClose={handleTestComplete} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="mock-tests-loading">
        <div className="animate-pulse text-slate-400 text-sm font-medium">Loading mock tests...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="mock-tests-page">
      {/* Header */}
      <div className="bg-[#0284C7] px-5 pt-6 pb-5">
        <div className="flex items-center gap-3 mb-1">
          <ClipboardList size={24} className="text-white" />
          <h1 className="text-lg font-black text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Mock Tests
          </h1>
        </div>
        <p className="text-xs text-white/70 ml-9">Practice with mock tests — boost your exam confidence</p>
      </div>

      {/* Available Tests */}
      <div className="px-4 pt-4 space-y-3">
        {tests.length === 0 ? (
          <div className="text-center py-16" data-testid="mock-tests-empty">
            <ClipboardList size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-500">No mock tests available yet</p>
            <p className="text-xs text-slate-400 mt-1">Check back soon for new practice tests</p>
          </div>
        ) : (
          tests.map((test) => (
            <div key={test.id} data-testid={`mock-test-card-${test.id}`} className="bg-white border border-slate-100 p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-50 flex-shrink-0 flex items-center justify-center rounded">
                  <CheckCircle size={18} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-slate-900">{test.title}</h3>
                  {test.description && <p className="text-xs text-slate-500 mt-0.5">{test.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <ClipboardList size={10} /> {test.question_count} questions
                    </span>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Clock size={10} /> {test.time_limit_minutes} min
                    </span>
                    <span className="text-[10px] text-slate-400">{test.total_marks} marks</span>
                  </div>
                </div>
              </div>
              <button
                data-testid={`start-test-${test.id}`}
                onClick={() => startTest(test)}
                className="w-full mt-3 bg-[#0284C7] text-white text-xs font-bold py-2.5 flex items-center justify-center gap-1.5 hover:bg-[#0369A1] transition-none"
              >
                <ArrowRight size={14} /> Start Test
              </button>
            </div>
          ))
        )}
      </div>

      {/* Test History */}
      {history.length > 0 && (
        <div className="px-4 mt-6">
          <button data-testid="toggle-history-btn" onClick={() => setShowHistory(!showHistory)} className="w-full flex items-center justify-between bg-white border border-slate-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <History size={16} className="text-[#0284C7]" />
              <span className="text-sm font-bold text-slate-900">My Test History</span>
              <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 font-semibold">{history.length}</span>
            </div>
            {showHistory ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showHistory && (
            <div className="space-y-2 mt-2" data-testid="test-history-list">
              {history.map((a, idx) => (
                <div key={a.id || idx} data-testid={`history-item-${idx}`} className="bg-white border border-slate-100 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-900 truncate">{a.test_title}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(a.submitted_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className="text-xs font-bold text-slate-700">{a.score}/{a.total_marks}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 ${a.percentage >= 60 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                        {a.percentage}%
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${a.percentage >= 60 ? "bg-green-500" : "bg-red-400"}`} style={{ width: `${a.percentage}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mock Test Player (Full-screen quiz-taking) ───
function MockTestPlayer({ test, onClose }) {
  const [questions] = useState(test.questions || []);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState((test.time_limit_minutes || 30) * 60);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (submitted) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [submitted]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const handleAnswer = (qIdx, answer) => {
    if (submitted) return;
    setAnswers({ ...answers, [String(qIdx)]: answer });
  };

  const handleSubmit = async () => {
    if (submitted) return;
    clearInterval(timerRef.current);
    setLoading(true);
    try {
      const { data } = await api.post(`/mock-tests/${test.id}/submit`, { answers });
      setResult(data);
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  const totalQ = questions.length;
  const answeredCount = Object.keys(answers).length;
  const isUrgent = timeLeft <= 60;

  // Result Screen
  if (submitted && result) {
    return (
      <div className="fixed inset-0 z-[999] bg-slate-900 overflow-y-auto" data-testid="mock-test-result">
        <div className="min-h-screen flex flex-col">
          <div className="px-4 py-3 bg-black/50 flex items-center gap-3">
            <button onClick={onClose} className="text-white" data-testid="result-close"><ArrowLeft size={20} /></button>
            <h1 className="text-sm font-bold text-white">Test Results</h1>
          </div>
          <div className="px-4 py-6 text-center">
            <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${result.percentage >= 60 ? "bg-green-500" : "bg-red-500"}`}>
              {result.percentage >= 60 ? <Trophy size={36} className="text-white" /> : <XCircle size={36} className="text-white" />}
            </div>
            <h2 className="text-2xl font-black text-white">{result.score}/{result.total_marks}</h2>
            <p className="text-lg font-bold text-white mt-1">{result.percentage}%</p>
            <p className="text-xs text-slate-400 mt-1">{result.correct}/{result.total_questions} correct</p>
          </div>
          <div className="px-4 pb-24">
            <h3 className="text-sm font-bold text-white mb-3">Answer Review</h3>
            {result.results?.map((r, i) => (
              <div key={i} data-testid={`result-q-${i}`} className={`mb-3 p-4 ${r.is_correct ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
                <div className="flex items-start gap-2 mb-2">
                  {r.is_correct ? <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" /> : <XCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />}
                  <p className="text-sm text-white font-medium">Q{i + 1}. {r.question_text}</p>
                </div>
                <div className="ml-6 space-y-1">
                  {["A", "B", "C", "D"].map((opt) => {
                    const optText = r[`option_${opt.toLowerCase()}`];
                    const isCorrect = opt === r.correct_answer;
                    const isUserPick = opt === r.user_answer;
                    return (
                      <div key={opt} className={`text-xs px-3 py-1.5 flex items-center gap-2 ${isCorrect ? "bg-green-500/20 text-green-300 font-bold" : isUserPick && !isCorrect ? "bg-red-500/20 text-red-300" : "text-slate-400"}`}>
                        <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[10px] flex-shrink-0">{opt}</span>
                        {optText}
                        {isCorrect && <CheckCircle size={12} className="ml-auto text-green-400" />}
                        {isUserPick && !isCorrect && <XCircle size={12} className="ml-auto text-red-400" />}
                      </div>
                    );
                  })}
                </div>
                {r.explanation && (
                  <div className="ml-6 mt-2 bg-blue-500/10 border border-blue-500/20 px-3 py-2 text-xs text-blue-300">
                    <strong>Explanation:</strong> {r.explanation}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-900 border-t border-slate-800">
            <button onClick={onClose} className="w-full bg-[#0284C7] text-white font-bold py-3 rounded-lg text-sm" data-testid="result-done-btn">Done</button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Taking
  const q = questions[currentQ];
  if (!q) return null;
  const options = [
    { key: "A", text: q.option_a },
    { key: "B", text: q.option_b },
    { key: "C", text: q.option_c },
    { key: "D", text: q.option_d },
  ];

  return (
    <div className="fixed inset-0 z-[999] bg-slate-900 flex flex-col" data-testid="mock-test-player">
      {/* Header */}
      <div className="px-4 py-3 bg-black/50 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => { if (window.confirm("Leave test? Progress will be lost.")) { clearInterval(timerRef.current); onClose(); } }} className="text-white" data-testid="leave-test-btn">
            <ArrowLeft size={20} />
          </button>
          <span className="text-xs text-white font-bold">{test.title}</span>
        </div>
        <div className={`px-3 py-1 rounded text-xs font-bold ${isUrgent ? "bg-red-500 text-white animate-pulse" : "bg-white/10 text-white"}`} data-testid="test-timer">
          <Clock size={12} className="inline mr-1" />{formatTime(timeLeft)}
        </div>
      </div>

      {/* Progress */}
      <div className="px-4 py-2 flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#0284C7] transition-none" style={{ width: `${((currentQ + 1) / totalQ) * 100}%` }} />
        </div>
        <span className="text-[10px] text-slate-400 font-bold">{currentQ + 1}/{totalQ}</span>
      </div>

      {/* Question */}
      <div className="flex-1 px-4 py-4 overflow-y-auto">
        <p className="text-white text-base font-medium mb-6" data-testid="question-text">Q{currentQ + 1}. {q.question_text}</p>
        <div className="space-y-3">
          {options.map(({ key, text }) => {
            const selected = answers[String(currentQ)] === key;
            return (
              <button
                key={key}
                data-testid={`option-${key}`}
                onClick={() => handleAnswer(currentQ, key)}
                className={`w-full text-left px-4 py-3.5 rounded-lg flex items-center gap-3 transition-none ${
                  selected ? "bg-[#0284C7] text-white" : "bg-white/10 text-white hover:bg-white/15"
                }`}
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${selected ? "bg-white text-[#0284C7]" : "bg-white/10"}`}>{key}</span>
                <span className="text-sm">{text}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="px-4 py-4 bg-black/30 flex items-center gap-3 flex-shrink-0">
        <button disabled={currentQ === 0} onClick={() => setCurrentQ(currentQ - 1)} className="flex-1 py-3 text-sm font-bold text-white bg-white/10 rounded-lg disabled:opacity-30" data-testid="prev-q-btn">
          Previous
        </button>
        {currentQ < totalQ - 1 ? (
          <button onClick={() => setCurrentQ(currentQ + 1)} className="flex-1 py-3 text-sm font-bold text-white bg-[#0284C7] rounded-lg" data-testid="next-q-btn">
            Next
          </button>
        ) : (
          <button onClick={() => { if (window.confirm(`Submit test? ${answeredCount}/${totalQ} answered.`)) handleSubmit(); }} disabled={loading} className="flex-1 py-3 text-sm font-bold text-white bg-green-600 rounded-lg disabled:opacity-50" data-testid="submit-test-btn">
            {loading ? "Submitting..." : "Submit Test"}
          </button>
        )}
      </div>

      {/* Question dots */}
      <div className="px-4 pb-3 bg-black/30">
        <div className="flex flex-wrap gap-1.5 justify-center">
          {questions.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentQ(i)}
              data-testid={`q-dot-${i}`}
              className={`w-6 h-6 rounded-full text-[9px] font-bold flex items-center justify-center ${
                i === currentQ ? "bg-[#0284C7] text-white" : answers[String(i)] ? "bg-green-500/30 text-green-400" : "bg-white/10 text-slate-500"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
